import { useCallback, useEffect, useState, type FormEvent } from "react";
import { type TagFacet, normalizeTag } from "../../shared/tags";
import { ApiError, api } from "../api";
import { Button, EmptyState, ErrorState, LoadingState } from "../components";
import "../styles/administration.css";

/**
 * The workspace's tag vocabulary on Settings: every category tag with its loop count, renamed or
 * merged inline, and the state tags listed muted below because they never become categories.
 * Reads GET /api/app/tags and writes POST /api/app/tags/rename (src/worker/tags.ts); a 404 from the
 * read renders the empty state so Settings works before the worker learns the route.
 */

type WorkspaceTags = { tags: TagFacet[]; stateTags: TagFacet[] };
type PanelState = "loading" | "ready" | "error";

function isNotFound(caught: unknown): boolean {
  return caught instanceof ApiError && caught.status === 404;
}

/** The sentence after a rename: a merge when the target already existed, otherwise a rename. */
export function renameOutcome(from: string, to: string, merged: boolean, loops: number): string {
  const count = `${loops} ${loops === 1 ? "loop" : "loops"}`;
  return merged ? `Merged ${from} into ${to} across ${count}.` : `Renamed ${from} to ${to} across ${count}.`;
}

export function TagVocabulary({ organizationId, canManage }: { organizationId: string; canManage: boolean }) {
  const [data, setData] = useState<WorkspaceTags>({ tags: [], stateTags: [] });
  const [state, setState] = useState<PanelState>("loading");
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");

  const load = useCallback(async () => {
    if (!organizationId) return;
    try {
      const result = await api<{ tags?: TagFacet[]; stateTags?: TagFacet[]; state?: TagFacet[] }>("/api/app/tags", {}, organizationId);
      setData({ tags: result.tags ?? [], stateTags: result.stateTags ?? result.state ?? [] });
      setState("ready");
    } catch (caught) {
      if (isNotFound(caught)) {
        setData({ tags: [], stateTags: [] });
        setState("ready");
        return;
      }
      setState("error");
    }
  }, [organizationId]);

  useEffect(() => {
    setState("loading");
    setEditing(null);
    setNotice("");
    setActionError("");
    void load();
  }, [load]);

  const target = normalizeTag(draft);
  const mergeTarget = editing && target && target !== editing ? data.tags.find((facet) => facet.tag === target) ?? null : null;
  const editingFacet = editing ? data.tags.find((facet) => facet.tag === editing) ?? null : null;

  function startEdit(tag: string) {
    setEditing(tag);
    setDraft(tag);
    setNotice("");
    setActionError("");
  }

  async function rename(event: FormEvent) {
    event.preventDefault();
    if (!editing || !target || target === editing) {
      setEditing(null);
      return;
    }
    setBusy(true);
    setActionError("");
    try {
      const result = await api<{ loops?: number }>("/api/app/tags/rename", { method: "POST", body: JSON.stringify({ from: editing, to: target }) }, organizationId);
      setNotice(renameOutcome(editing, target, Boolean(mergeTarget), result.loops ?? editingFacet?.count ?? 0));
      setEditing(null);
      await load();
    } catch (caught) {
      setActionError(isNotFound(caught) ? "Renaming tags is not available yet." : caught instanceof Error ? caught.message : `${editing} could not be renamed`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel admin-surface admin-tags-panel" id="tags" aria-labelledby="tags-heading">
      <header className="admin-surface__head">
        <div><h2 className="eyebrow" id="tags-heading">Tags</h2><p>Category tags come from your agents, never from LoopGroup. Renaming a tag changes it on every loop; renaming it to a tag that already exists merges the two. Sharing filters and rules use exactly this list.</p></div>
        {state === "ready" && data.tags.length > 0 && <span className="admin-surface__count">{data.tags.length} {data.tags.length === 1 ? "tag" : "tags"}</span>}
      </header>
      {state === "loading" && <LoadingState>Reading the tag vocabulary…</LoadingState>}
      {state === "error" && <ErrorState action={<Button variant="secondary" onClick={() => { setState("loading"); void load(); }}>Try again</Button>}>The tag vocabulary could not be loaded.</ErrorState>}
      {state === "ready" && data.tags.length === 0 && <EmptyState eyebrow="No category tags yet" title="Ask an agent to categorize the catalog">The Sources page has a prompt that asks your agent to propose a small vocabulary and apply it only after you approve.</EmptyState>}
      {state === "ready" && data.tags.length > 0 && (
        <div className="admin-tag-list">
          {data.tags.map((facet) => (
            <article key={facet.tag} className={editing === facet.tag ? "is-editing" : ""}>
              {editing === facet.tag ? (
                <form className="admin-tag-rename" onSubmit={(event) => void rename(event)} aria-label={`Rename ${facet.tag}`}>
                  <input value={draft} onChange={(event) => setDraft(event.target.value)} aria-label="New tag name" autoFocus maxLength={40} required />
                  <Button type="submit" busy={busy} disabled={!target || target === editing}>{mergeTarget ? "Merge" : "Rename"}</Button>
                  <Button type="button" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                  <small>{mergeTarget ? `Merges into ${mergeTarget.tag}, which already tags ${mergeTarget.count} ${mergeTarget.count === 1 ? "loop" : "loops"}.` : target && target !== editing ? `Renames ${facet.count} ${facet.count === 1 ? "loop" : "loops"} to ${target}.` : "Lowercase slugs only; spaces become hyphens."}</small>
                </form>
              ) : (
                <>
                  <code className="admin-tag">{facet.tag}</code>
                  <span className="admin-tag-count"><strong>{facet.count}</strong> {facet.count === 1 ? "loop" : "loops"}</span>
                  {canManage ? <Button variant="ghost" onClick={() => startEdit(facet.tag)} aria-label={`Rename ${facet.tag}`}>Rename</Button> : <span />}
                </>
              )}
            </article>
          ))}
        </div>
      )}
      {state === "ready" && data.stateTags.length > 0 && (
        <div className="admin-tag-state">
          <p>State tags stay on loops for search and never appear as categories, facets, or rule suggestions.</p>
          <div aria-label="State tags">{data.stateTags.map((facet) => <span key={facet.tag}>{facet.tag} <b>{facet.count}</b></span>)}</div>
        </div>
      )}
      {notice && <p className="admin-tags-panel__notice" role="status">{notice}</p>}
      {actionError && <div className="form-error admin-tags-panel__error" role="alert">{actionError}</div>}
    </section>
  );
}
